import * as api from '@api';
import * as constants from '@constants';
import { useEffect, useState } from 'react';
import { useQuery } from '@/hooks/useApi';
import useTranslator from '@/hooks/useTranslator';

const useBuilderSidebar = () => {
  const { getTranslation } = useTranslator({ page: 'questionnaire' });

  const [isHideSideBar, setIsHideSideBar] = useState(true);
  const [filteredQuestions, setFilteredQuestions] = useState(null);
  const [questionTemplates, setQuestionTemplates] = useState([]);
  const [categories, setCategories] = useState([]);

  const { data: dataPointTemplates, isFetching: fetchingTemplates } = useQuery(
    'questionnaireDataPointsGroupTemplate',
    api.backend.questionnaireDataPointTemplates,
  );

  useEffect(() => {
    // `{metadata, data}` envelope — the groups are one level deeper than they
    // were on the legacy `/v1/data-points?groupByTemplates=true` route.
    const groups = dataPointTemplates?.data?.data;
    if (groups) setCategories(groups);
  }, [dataPointTemplates]);

  const { data: impactAreasGeneral } = useQuery(
    ['getImpactAreasGeneral'],
    () => api.companies.getImpactAreasBySectionId(1),
    {},
  );
  const { data: impactAreasEnvironmental } = useQuery(
    ['getImpactAreasEnvironmental'],
    () => api.companies.getImpactAreasBySectionId(2),
    {},
  );

  const filterObjects = (data, keyword) => {
    if (keyword.length < 1) return null;
    return data.reduce((filteredData, obj) => {
      if (obj.name.toLowerCase().includes(keyword.toLowerCase())) {
        filteredData.push({ ...obj, isExpanded: true });
      } else if (obj.children) {
        const filteredChildren = filterObjects(obj.children, keyword);
        if (filteredChildren.length > 0) {
          filteredData.push({
            ...obj,
            children: filteredChildren,
            isExpanded: true,
          });
        }
      }
      return filteredData;
    }, []);
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const handleInputChange = debounce(value => {
    setFilteredQuestions(filterObjects([...questionTemplates], value));
  }, 500);

  const buildHierarchy = data => {
    const idToNodeMap = {};
    const hierarchy = [];

    data.forEach(item => {
      idToNodeMap[item.id] = {
        id: item.id,
        name: item.name,
        isParent: item.isParent === 1,
        children: [],
      };
    });

    data.forEach(item => {
      const currentNode = idToNodeMap[item.id];
      if (item.parentId) {
        const parent = idToNodeMap[item.parentId];
        if (
          parent &&
          !parent.children.some(child => child.id === currentNode.id)
        ) {
          parent.children.push(currentNode);
        }
      } else if (!hierarchy.some(node => node.id === currentNode.id)) {
        hierarchy.push(currentNode);
      }
    });

    return hierarchy;
  };

  useEffect(() => {
    if (
      !categories?.length ||
      !impactAreasGeneral?.data ||
      !impactAreasEnvironmental?.data
    ) {
      return;
    }

    const impactAreas = [
      ...impactAreasGeneral.data,
      ...impactAreasEnvironmental.data,
    ];
    const treeData = buildHierarchy(impactAreas);

    const customQuestions =
      constants.supplierQuestionnaireAnswerTypes.selectArrayOptions
        .filter(
          question =>
            !['ASSET_LINK', 'MULTIPLE_CHOICE', 'CHECK_BOX', 'BOOLEAN'].includes(
              question.key,
            ),
        )
        .map(answerType => ({
          name: answerType.text,
          id: null,
          metadata: {
            questionTitle: '',
            description: '',
            answerType,
            icon: constants.customQuestionTypesIcons[answerType.key],
            isCustom: true,
            options: [],
          },
        }));

    const questions = treeData
      .map((template, index) => templateTree(template, `${index + 1}`))
      .filter(Boolean);

    setQuestionTemplates([
      ...questions,
      {
        name: 'Custom',
        id: 'custom',
        isExpanded: false,
        metadata: {
          icon: 'fal fa-light fa-tools',
          isOpenIcon: 'fal fa-solid fa-tools',
        },
        children: customQuestions,
      },
    ]);
  }, [categories, impactAreasEnvironmental, impactAreasGeneral]);

  const setExpandedStatus = (data, targetId) => {
    return data.map(item => {
      if (item.id === targetId) {
        return { ...item, isExpanded: !item.isExpanded };
      }
      if (item.children?.length) {
        return {
          ...item,
          children: setExpandedStatus(item.children, targetId),
        };
      }
      return item;
    });
  };

  const templateTree = (node, layerNumber) => {
    if (node.isParent) {
      const children =
        node.children
          ?.map((child, index) =>
            templateTree(child, `${layerNumber}.${index + 1}`),
          )
          .filter(Boolean) || [];

      if (!children.length) return null;

      return {
        name: node.name,
        id: node.id,
        layerNumber,
        metadata: { icon: 'fal fa-globe', isOpenIcon: 'fas fa-globe' },
        children,
      };
    }

    const template = categories.find(({ category }) => category === node.id);
    if (!template) return null;

    return {
      name: node.name,
      id: template.category,
      layerNumber,
      metadata: { icon: 'fal fa-globe', isOpenIcon: 'fas fa-globe' },
      children: template.dataPoints.map(
        ({ id, name, fieldTypeId, options, retracedId, ...otherProps }) => ({
          name,
          id,
          metadata: {
            ...otherProps,
            questionTitle: name,
            options,
            templateId: id,
            retracedDatapointId: retracedId,
            isCustom: false,
            isOptionMapped: true,
            answerType: {
              key: fieldTypeId,
              text: constants.companyDataFieldsTypes.raw[fieldTypeId],
            },
            retracedTemplateId: template.category,
          },
        }),
      ),
    };
  };

  const fetchingTemplateData = (data, targetId) => {
    return data.map(item => {
      if (item.id === targetId) {
        return {
          ...item,
          isExpanded: !item.isExpanded,
          children: [
            { name: 'data1', id: '3' },
            { name: 'data2', id: '4' },
          ],
        };
      }
      if (item.children?.length) {
        return {
          ...item,
          children: fetchingTemplateData(item.children, targetId),
        };
      }
      return item;
    });
  };
  return {
    filteredQuestions,
    questionTemplates,
    getTranslation,
    handleInputChange,
    setQuestionTemplates,
    setExpandedStatus,
    isHideSideBar,
    setIsHideSideBar,
    fetchingTemplateData,
    fetchingTemplates,
  };
};

export default useBuilderSidebar;
